import { useCallback, useEffect, useRef, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, X, Upload } from "lucide-react";
import { formatPrice } from "@/data/products";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CATEGORIES = ["T-Shirts", "Hoodies", "Accessories", "Other"];

const EMPTY_FORM = {
  title: "",
  price: "",
  category: "T-Shirts",
  badge: "",
  description: "",
  image: "",
  secondaryImage: "",
  stock: "",
};

const ImagePicker = ({ label, value, onChange, token, testid }) => {
  const inputRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const form = new FormData();
      form.append("file", file);
      const { data } = await axios.post(`${API}/admin/upload`, form, {
        headers: { Authorization: `Bearer ${token}` },
      });
      onChange(data.url);
      toast.success(`${label} uploaded`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div>
      <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">{label}</p>
      <div className="flex items-center gap-3">
        {value ? (
          <img src={value} alt={label} className="h-20 w-16 border border-zinc-200 object-cover" data-testid={`${testid}-preview`} />
        ) : (
          <div className="flex h-20 w-16 items-center justify-center border border-dashed border-zinc-300 text-zinc-300">
            <Upload size={16} />
          </div>
        )}
        <div className="flex-1">
          <input
            ref={inputRef}
            type="file"
            accept="image/*"
            onChange={handleFile}
            data-testid={`${testid}-file`}
            className="w-full font-body text-xs text-zinc-500 file:mr-3 file:border file:border-zinc-300 file:bg-white file:px-3 file:py-2 file:font-heading file:text-[10px] file:font-bold file:uppercase file:tracking-widest file:text-zinc-700 hover:file:border-tiger hover:file:text-tiger"
          />
          {uploading && <p className="mt-1 font-body text-xs text-zinc-400">Uploading...</p>}
          <input
            type="text"
            value={value}
            onChange={(e) => onChange(e.target.value)}
            placeholder="…or paste an image URL"
            data-testid={`${testid}-url`}
            className="mt-2 w-full border border-zinc-300 px-3 py-2 font-body text-xs text-zinc-900 outline-none focus:border-tiger"
          />
        </div>
      </div>
    </div>
  );
};

export const AdminProducts = ({ token }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [formOpen, setFormOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);

  const headers = { Authorization: `Bearer ${token}` };

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/products`);
      setProducts(data);
    } catch {
      toast.error("Could not load products");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditingId(null);
    setForm(EMPTY_FORM);
    setFormOpen(true);
  };

  const openEdit = (p) => {
    setEditingId(p.id);
    setForm({
      title: p.title,
      price: String(p.price),
      category: p.category || "T-Shirts",
      badge: p.badge || "",
      description: p.description || "",
      image: p.image || "",
      secondaryImage: p.secondaryImage || "",
      stock: p.stock == null ? "" : String(p.stock),
    });
    setFormOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!form.image) {
      toast.error("Add a main product image first");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, price: parseFloat(form.price), secondaryImage: form.secondaryImage || form.image, stock: form.stock === "" ? null : parseInt(form.stock, 10) };
      if (editingId) {
        await axios.put(`${API}/admin/products/${editingId}`, payload, { headers });
        toast.success("Product updated");
      } else {
        await axios.post(`${API}/admin/products`, payload, { headers });
        toast.success("Product added — it's live in the shop");
      }
      setFormOpen(false);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Delete "${p.title}" from the shop? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`, { headers });
      toast.success("Product deleted");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Delete failed");
    }
  };

  return (
    <div data-testid="admin-products-tab">
      <div className="mb-6 flex items-center justify-between">
        <p className="font-body text-sm text-zinc-500">{products.length} product(s) in the shop</p>
        <button
          onClick={openCreate}
          data-testid="add-product-btn"
          className="flex items-center gap-2 bg-tiger px-5 py-2.5 font-heading text-xs font-bold uppercase tracking-widest text-black hover:bg-tiger-hover"
        >
          <Plus size={14} /> Add Product
        </button>
      </div>

      {formOpen && (
        <form onSubmit={handleSave} data-testid="product-form" className="mb-8 border border-zinc-200 bg-white p-6">
          <div className="mb-5 flex items-center justify-between">
            <h3 className="font-heading text-sm font-bold uppercase tracking-widest text-zinc-900">
              {editingId ? "Edit Product" : "New Product"}
            </h3>
            <button type="button" onClick={() => setFormOpen(false)} data-testid="product-form-close" className="text-zinc-400 hover:text-tiger" aria-label="Close form">
              <X size={18} />
            </button>
          </div>
          <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
            <div>
              <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">Title</p>
              <input required value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="product-title-input"
                className="w-full border border-zinc-300 px-3 py-2.5 font-body text-sm outline-none focus:border-tiger" placeholder="Fierce Tiger Tee" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">Price (£)</p>
                <input required type="number" min="0.5" step="0.01" value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="product-price-input"
                  className="w-full border border-zinc-300 px-3 py-2.5 font-body text-sm outline-none focus:border-tiger" placeholder="34.99" />
              </div>
              <div>
                <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">Category</p>
                <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="product-category-select"
                  className="w-full border border-zinc-300 bg-white px-3 py-2.5 font-body text-sm outline-none focus:border-tiger">
                  {CATEGORIES.map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>
            <div>
              <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">Badge (optional)</p>
              <input value={form.badge} onChange={(e) => setForm({ ...form, badge: e.target.value })} data-testid="product-badge-input"
                className="w-full border border-zinc-300 px-3 py-2.5 font-body text-sm outline-none focus:border-tiger" placeholder="NEW DROP" />
            </div>
            <div>
              <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">Stock (blank = unlimited)</p>
              <input type="number" min="0" step="1" value={form.stock} onChange={(e) => setForm({ ...form, stock: e.target.value })} data-testid="product-stock-input"
                className="w-full border border-zinc-300 px-3 py-2.5 font-body text-sm outline-none focus:border-tiger" placeholder="e.g. 25" />
            </div>
            <div>
              <p className="mb-2 font-heading text-xs font-bold uppercase tracking-widest text-zinc-700">Description</p>
              <textarea rows={2} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="product-description-input"
                className="w-full border border-zinc-300 px-3 py-2.5 font-body text-sm outline-none focus:border-tiger" placeholder="Heavyweight cotton, oversized fit..." />
            </div>
            <ImagePicker label="Main image" value={form.image} onChange={(v) => setForm({ ...form, image: v })} token={token} testid="product-image" />
            <ImagePicker label="Hover image (optional)" value={form.secondaryImage} onChange={(v) => setForm({ ...form, secondaryImage: v })} token={token} testid="product-secondary-image" />
          </div>
          <button type="submit" disabled={saving} data-testid="product-save-btn"
            className="mt-6 bg-zinc-900 px-8 py-3 font-heading text-xs font-bold uppercase tracking-widest text-white hover:bg-tiger hover:text-black disabled:opacity-60">
            {saving ? "Saving..." : editingId ? "Save Changes" : "Add To Shop"}
          </button>
        </form>
      )}

      {loading ? (
        <p className="py-16 text-center font-body text-sm text-zinc-500">Loading products...</p>
      ) : products.length === 0 ? (
        <p className="border border-zinc-200 bg-white py-16 text-center font-body text-sm text-zinc-500" data-testid="products-empty">
          No products yet — press Add Product to create the first one.
        </p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="admin-products-grid">
          {products.map((p) => (
            <div key={p.id} className="flex gap-4 border border-zinc-200 bg-white p-4" data-testid={`admin-product-${p.id}`}>
              <img src={p.image} alt={p.title} className="h-24 w-20 border border-zinc-100 object-cover" />
              <div className="flex flex-1 flex-col">
                <p className="font-heading text-sm font-semibold text-zinc-900">{p.title}</p>
                <p className="mt-0.5 font-body text-xs text-zinc-500">{p.category} · <span className="uppercase">{p.source || "manual"}</span></p>
                <p className="mt-1 font-heading text-sm font-bold text-tiger">{formatPrice(p.price)}</p>
                <p className={`mt-0.5 font-body text-[10px] font-semibold uppercase tracking-widest ${
                  p.stock === 0 ? "text-red-500" : p.stock != null && p.stock <= 5 ? "text-tiger" : "text-zinc-400"
                }`} data-testid={`stock-label-${p.id}`}>
                  {p.stock == null ? "Stock: unlimited" : p.stock === 0 ? "Sold out" : `Stock: ${p.stock}`}
                </p>
                <div className="mt-auto flex gap-2 pt-2">
                  <button onClick={() => openEdit(p)} data-testid={`edit-product-${p.id}`}
                    className="flex items-center gap-1 border border-zinc-300 px-3 py-1.5 font-heading text-[10px] font-bold uppercase tracking-widest text-zinc-600 hover:border-tiger hover:text-tiger">
                    <Pencil size={11} /> Edit
                  </button>
                  <button onClick={() => handleDelete(p)} data-testid={`delete-product-${p.id}`}
                    className="flex items-center gap-1 border border-red-200 px-3 py-1.5 font-heading text-[10px] font-bold uppercase tracking-widest text-red-500 hover:bg-red-600 hover:text-white">
                    <Trash2 size={11} /> Delete
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
